import { formatEther } from 'viem';
import { MIN_LOCK_DAYS_FALLBACK, MAX_LOCK_DAYS_FALLBACK, MIN_MULTIPLIER, MAX_MULTIPLIER, type PositionInfo } from '../constants';

/**
 * Format token amounts from bigint to human-readable string
 * @param amount - Token amount in wei
 * @returns Formatted string with locale
 */
export const formatTokenAmount = (amount: bigint | undefined): string => {
  if (!amount) return '0.00';
  const formatted = formatEther(amount);
  const num = parseFloat(formatted);
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

/**
 * Calculate multiplier based on lock days (linear interpolation)
 * DEPRECATED: Use previewPosition from contract for accurate multiplier
 * This is a client-side approximation only
 * @param days - Number of days to lock
 * @returns Multiplier value (1.0 - 2.0)
 */
export const calculateMultiplier = (days: number): number => {
  if (days < MIN_LOCK_DAYS_FALLBACK) return MIN_MULTIPLIER;
  if (days > MAX_LOCK_DAYS_FALLBACK) return MAX_MULTIPLIER;

  const ratio = (days - MIN_LOCK_DAYS_FALLBACK) / (MAX_LOCK_DAYS_FALLBACK - MIN_LOCK_DAYS_FALLBACK);
  return MIN_MULTIPLIER + ratio * (MAX_MULTIPLIER - MIN_MULTIPLIER);
};

/**
 * Calculate APY (approximate based on emission rate)
 * @param totalStaked - Total staked amount
 * @param emissionRate - Emission rate per second
 * @returns APY as formatted string
 */
export const calculateAPY = (totalStaked: bigint | undefined, emissionRate: bigint | undefined): string => {
  if (!totalStaked || !emissionRate || totalStaked === 0n) return '0.00';

  // Annual rewards = emissionPerSecond * seconds_per_year
  const secondsPerYear = 365n * 24n * 60n * 60n;
  const annualRewards = emissionRate * secondsPerYear;

  // APY = (annualRewards / totalStaked) * 100 * averageMultiplier
  const averageMultiplier = (MIN_MULTIPLIER + MAX_MULTIPLIER) / 2;
  const apy = (Number(formatEther(annualRewards)) / Number(formatEther(totalStaked))) * 100 * averageMultiplier;

  return apy.toFixed(2);
};

/**
 * Check if position can be withdrawn (lock period expired)
 * @param position - Position info from contract
 * @returns True if position can be withdrawn
 */
export const canWithdraw = (position: PositionInfo): boolean => {
  if (!position) return false;

  const currentTime = BigInt(Math.floor(Date.now() / 1000));
  return currentTime >= position.unlockTime;
};

/**
 * Get remaining lock time in human-readable format
 * @param unlockTime - Unix timestamp of unlock time
 * @returns Formatted remaining time
 */
export const getRemainingTime = (unlockTime: bigint): string => {
  const currentTime = BigInt(Math.floor(Date.now() / 1000));

  if (currentTime >= unlockTime) {
    return 'Unlocked';
  }

  const remaining = Number(unlockTime - currentTime);
  const days = Math.floor(remaining / (24 * 60 * 60));
  const hours = Math.floor((remaining % (24 * 60 * 60)) / (60 * 60));

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  return `${hours}h`;
};
